import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { unstable_useId as useId } from '@mui/material/utils'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CoachName from 'src/legacy/components/CoachName/CoachName'
import { useSessionUID } from 'src/session/useSession'
import { describeFailure, Failure } from '../../api/failureReason'
import FailureMessage from '../../components/FailureMessage'
import { AdvancedCareTeamMember } from '../../api/types'
import { CoachOption, useCoachRoster } from '../../api/useCoachRoster'
import DialogHeader from '../../components/DialogHeader'
import { formatDay } from '../../format'
import { advancedColors } from '../../theme'
import ProviderPicker from './ProviderPicker'
import { ROLE_CATEGORIES, roleLabelFor } from './roleCategories'
import { CareTeamTemplate, RoleCategoryId, groupMembersByCategory } from './types'

const addControlsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  '& .MuiAutocomplete-root': { flex: 1, minWidth: 0 },
  '& .MuiTextField-root': { mt: 0 },
  '& .MuiOutlinedInput-root': { height: 40, borderRadius: '12px' },
  '& .MuiButton-root': { height: 40, minWidth: 96, flexShrink: 0, borderRadius: '12px' },
}

const ConfirmRemoveProviderDialog = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogHeader title="Remove this provider?" onClose={onCancel} />
    <DialogContent>
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted, mb: 2 }}>
        This ends their membership on this patient&apos;s care team.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button color="error" variant="contained" onClick={onConfirm}>
        Remove
      </Button>
    </DialogActions>
  </Dialog>
)

const MemberRow = ({
  member,
  editMode,
  disabled,
  onRemove,
}: {
  member: AdvancedCareTeamMember
  editMode: boolean
  disabled: boolean
  onRemove: () => void
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const sessionUid = useSessionUID()
  // Removing yourself would end your own access to this patient mid-dialog.
  const isSelf = member.providerUid === sessionUid

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CoachName
          coachID={member.providerUid}
          renderLabel={(name) => (
            <Typography
              variant="body2"
              sx={{ color: advancedColors.inkMuted, fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {name}
            </Typography>
          )}
        />
        <Typography variant="caption" sx={{ display: 'block', color: advancedColors.inkMuted }}>
          Since {formatDay(member.startedAt)}
        </Typography>
      </Box>
      {editMode && !isSelf && (
        <Tooltip title="Remove this provider">
          <IconButton
            size="small"
            aria-label="Remove this provider"
            disabled={disabled}
            onClick={() => setConfirmOpen(true)}
          >
            <DeleteOutlineRoundedIcon fontSize="small" sx={{ color: advancedColors.inkMuted }} />
          </IconButton>
        </Tooltip>
      )}
      <ConfirmRemoveProviderDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          onRemove()
        }}
      />
    </Box>
  )
}

const AddMemberRow = ({
  coaches,
  disabled,
  onAdd,
  onAdded,
}: {
  coaches: CoachOption[]
  disabled: boolean
  onAdd: (providerUid: string) => Promise<boolean>
  onAdded: () => void
}) => {
  const [coachId, setCoachId] = useState<string | null>(null)

  return (
    <Box sx={addControlsSx}>
      <ProviderPicker
        label="Add a provider"
        options={coaches}
        value={coachId}
        onChange={setCoachId}
        disabled={disabled}
        autoFocus
      />
      <Button
        size="small"
        variant="outlined"
        disabled={!coachId || disabled}
        onClick={async () => {
          if (coachId && (await onAdd(coachId))) {
            onAdded()
          }
        }}
      >
        Add
      </Button>
    </Box>
  )
}

type CategoryGroupRowProps = {
  categoryLabel: string
  members: AdvancedCareTeamMember[]
  coaches: CoachOption[]
  editMode: boolean
  saving: boolean
  onAddMember: (providerUid: string) => Promise<boolean>
  onRemoveMember: (memberId: string) => void
  onRemoveCategory: () => void
}

const CategoryGroupRow = ({
  categoryLabel,
  members,
  coaches,
  editMode,
  saving,
  onAddMember,
  onRemoveMember,
  onRemoveCategory,
}: CategoryGroupRowProps) => {
  const [addingMember, setAddingMember] = useState(false)
  const addMemberId = useId()
  // Unique on (uid, provider_uid, role).
  const availableCoaches = coaches.filter((coach) => !members.some((member) => member.providerUid === coach.id))

  return (
    <Paper
      component="section"
      aria-label={categoryLabel}
      sx={{
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          minHeight: 44,
          bgcolor: advancedColors.surface,
          borderBottom: `1px solid ${advancedColors.line}`,
        }}
      >
        <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, color: advancedColors.ink, flex: 1 }}>
          {categoryLabel}
        </Typography>
        {editMode && members.length === 0 && (
          <Tooltip title="Remove this role">
            <IconButton size="small" aria-label="Remove this role" disabled={saving} onClick={onRemoveCategory}>
              <DeleteOutlineRoundedIcon fontSize="small" sx={{ color: advancedColors.inkMuted }} />
            </IconButton>
          </Tooltip>
        )}
        {editMode && (
          <Tooltip title={addingMember ? 'Cancel adding a provider' : 'Add a provider'}>
            <IconButton
              size="small"
              aria-label={`${addingMember ? 'Cancel adding a provider' : 'Add a provider'} to ${categoryLabel}`}
              aria-expanded={addingMember}
              aria-controls={addingMember ? addMemberId : undefined}
              disabled={saving}
              onClick={() => setAddingMember((current) => !current)}
              sx={{ color: advancedColors.ink }}
            >
              {addingMember ? <CloseRoundedIcon fontSize="small" /> : <AddRoundedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ px: 2, py: 1.5 }}>
        {editMode && addingMember && (
          <Box id={addMemberId} sx={{ pb: 1.5, mb: 1, borderBottom: `1px solid ${advancedColors.line}` }}>
            <AddMemberRow
              coaches={availableCoaches}
              disabled={saving}
              onAdd={onAddMember}
              onAdded={() => setAddingMember(false)}
            />
          </Box>
        )}
        {members.length === 0 ? (
          <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
            Unassigned
          </Typography>
        ) : (
          members.map((member) => (
            <MemberRow
              key={member.memberId}
              member={member}
              editMode={editMode}
              disabled={saving}
              onRemove={() => onRemoveMember(member.memberId)}
            />
          ))
        )}
      </Box>
    </Paper>
  )
}

const AddRoleMenu = ({
  existingIds,
  onAddCategory,
  disabled,
}: {
  existingIds: RoleCategoryId[]
  onAddCategory: (categoryId: RoleCategoryId) => void
  disabled: boolean
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const menuId = useId()
  // Built-in roles only: a custom one would reach the server as a slug other browsers can't label.
  const available = ROLE_CATEGORIES.filter((candidate) => !existingIds.includes(candidate.id))

  return (
    <>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddRoundedIcon fontSize="small" />}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? menuId : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Add a role
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-label': 'Available roles' }}
        PaperProps={{ sx: { width: 220, boxSizing: 'border-box', mt: 1 } }}
      >
        {available.length === 0 ? (
          <MenuItem disabled>All roles are already on this team.</MenuItem>
        ) : (
          available.map((category) => (
            <MenuItem
              key={category.id}
              onClick={() => {
                onAddCategory(category.id)
                setAnchor(null)
              }}
            >
              {category.label}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  )
}

type Props = {
  open: boolean
  onClose: () => void
  editMode: boolean
  template: CareTeamTemplate
  members: AdvancedCareTeamMember[]
  onAddCategory: (categoryId: RoleCategoryId) => void
  onRemoveCategory: (categoryId: RoleCategoryId) => void
  onAddMember: (categoryId: RoleCategoryId, providerUid: string) => Promise<void>
  onRemoveMember: (memberId: string) => Promise<void>
}

const ManageCareTeamDialog = ({
  open,
  onClose,
  editMode,
  template,
  members,
  onAddCategory,
  onRemoveCategory,
  onAddMember,
  onRemoveMember,
}: Props) => {
  const coaches = useCoachRoster()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<Failure | null>(null)
  const groups = groupMembersByCategory(template.categoryIds, members)

  const save = async (write: () => Promise<void>): Promise<boolean> => {
    setSaving(true)
    setSaveError(null)
    try {
      await write()
      return true
    } catch (error) {
      setSaveError(describeFailure('Failed to update the care team.', error))
      return false
    } finally {
      setSaving(false)
    }
  }

  const rowFor = (group: (typeof groups)[number]): CategoryGroupRowProps => ({
    categoryLabel: roleLabelFor(group.categoryId),
    members: group.members,
    coaches,
    editMode,
    saving,
    onAddMember: (providerUid) => save(() => onAddMember(group.categoryId, providerUid)),
    onRemoveMember: async (memberId) => {
      await save(() => onRemoveMember(memberId))
    },
    onRemoveCategory: () => {
      if (group.members.length === 0 && !saving) {
        onRemoveCategory(group.categoryId)
      }
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="care-team-title">
      <DialogHeader
        id="care-team-title"
        title="Care team"
        onClose={onClose}
        action={
          editMode && (
            <AddRoleMenu
              existingIds={groups.map((group) => group.categoryId)}
              onAddCategory={onAddCategory}
              disabled={saving}
            />
          )
        }
      />
      <DialogContent
        sx={{
          bgcolor: advancedColors.greenSoft,
          minHeight: 0,
          maxHeight: '65vh',
          overflowY: 'auto',
        }}
      >
        {saveError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <FailureMessage failure={saveError} />
          </Alert>
        )}

        {groups.length === 0 && (
          <Typography variant="body2" sx={{ color: advancedColors.inkMuted, mt: 1 }}>
            {editMode ? 'Add a role to get started.' : 'No roles have been set up for this program yet.'}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.map((group) => (
            <CategoryGroupRow key={group.categoryId} {...rowFor(group)} />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ManageCareTeamDialog
